/**
 * ChatInput -- multi-line text input with Enter to send, Shift+Enter for newline.
 *
 * Auto-resizes textarea height based on content (capped at 120px).
 * Send button uses primary color with an arrow-up icon.
 */

import { useState, useRef, useCallback } from "react";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
}

export function ChatInput({ onSend, disabled }: ChatInputProps): React.ReactElement {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resetHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "44px";
    }
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setInput("");
    resetHeight();
  }, [input, disabled, onSend, resetHeight]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "44px";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  return (
    <div className="flex items-end gap-2 px-4 py-3 border-t border-border bg-bg-secondary">
      <textarea
        ref={textareaRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        placeholder="Message Atlas..."
        disabled={disabled}
        rows={1}
        className="flex-1 resize-none bg-surface rounded-xl px-4 py-2.5 text-base md:text-sm text-text-primary placeholder:text-text-tertiary border border-border focus:border-primary focus:outline-none min-h-[44px] max-h-[120px]"
      />
      <button
        onClick={handleSend}
        disabled={!input.trim() || disabled}
        className="btn-primary !h-[44px] !w-[44px] !p-0 shrink-0 rounded-xl"
        aria-label="Send message"
      >
        {/* Arrow up icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      </button>
    </div>
  );
}
